/**
 * The job page — WO → SERVICE. The header card is the work-order identity
 * (vehicle, number, who owns it); one card per service with the manager's
 * description, the parts, the state's control and, when expanded, manual
 * time entry + Finish. When every service is done, the completion banner
 * replaces the cards. The job's own notes (the advisor's intake note, the
 * crew's additions) sit in their own panel beside the job at `lg` and up —
 * "to make it easier to distinguish" (2026-09-22) — sticky to the top so
 * they stay in view while the services scroll; below `lg` they stack
 * between the header and the services, same as before.
 *
 * The whole-job claim ("Auftrag übernehmen") lives on the Today board card
 * only (TodayRoute.tsx) — this page is reachable at all only once the tech
 * already holds a stake in the job (the backend gates
 * `/work-orders/{id}/detail` on the same predicate `hasStake` names), so a
 * whole-job claim box here could never be offered in practice. What this
 * page CAN still offer is a per-service Take on a service that is still
 * loose alongside the one that gave the tech their stake — see
 * `WoServiceCard`. Nothing is shown as a step — see serviceView.ts.
 *
 * No prices anywhere — the tech side is time only, and WO "time" figures
 * are the signed-in tech's own (see woDetail.ts).
 */
import { useEffect, useState } from 'react'
import { FEATURE_TECH_ADD_SERVICE } from '../../featureFlags'
import { ArrowLeft, Plus } from '@phosphor-icons/react'
import { useNavigate, useParams } from 'react-router-dom'
import { cn } from '@vollgas/shared/utils'
import {
  useClaimService,
  useCompleteWorkOrder,
  usePauseTask,
  useStartTask,
  useWorkOrderDetail,
} from '@/hooks/useWorkshop'
import { useCurrentUser } from '@/auth/SessionContext'
import { useI18n } from '@/i18n/TranslationContext'
import { WoHeaderCard } from './WoHeaderCard'
import { WoJobNotes } from './WoJobNotes'
import { WoServiceCard } from './WoServiceCard'
import { WoCompletion } from './WoCompletion'
import { ProposeServiceModal } from './ProposeServiceModal'
import { FAST_TICK, SLOW_TICK, useNow } from '@vollgas/shared/hooks'
import { useActionToast } from '@/components/ActionToast'
import { defaultExpandedId, hasMyRunningTask, hasStake, isAllDone } from './woDetail'
import { markNotesSeen } from './notesSeenStore'

export function WorkOrderDetailRoute() {
  const { id = '' } = useParams<{ id: string }>()
  const { t } = useI18n()
  const navigate = useNavigate()
  const toast = useActionToast()
  const meId = useCurrentUser()?.id ?? null
  const { data: wo, isLoading, isError } = useWorkOrderDetail(id)
  const claimService = useClaimService()
  const start = useStartTask()
  const pause = usePauseTask()
  const completeWo = useCompleteWorkOrder()
  // Fast only while my timer runs — see useNow. `wo` is undefined until the
  // query resolves, and nothing is ticking then anyway.
  const now = useNow(wo && hasMyRunningTask(wo) ? FAST_TICK : SLOW_TICK)
  // `undefined` = follow the auto-expand default; a string / null = user choice.
  const [expandChoice, setExpandChoice] = useState<string | null | undefined>(undefined)
  const [proposeOpen, setProposeOpen] = useState(false)

  // Opening the job is reading its notes — so the Today board's badge goes
  // out. Fires once per job per mount; a note written while this page is
  // open is still unread next time, since the stamp does not move again
  // until the effect re-runs. Mirrors apps/management's WorkOrderDetail.
  useEffect(() => {
    if (wo) markNotesSeen(meId, wo.id)
  }, [meId, wo?.id])

  if (isLoading) return <div role="status" className="py-12 text-center text-sm text-glass-text-muted">{t.home.loading}</div>
  if (isError || !wo) return <div role="alert" className="py-12 text-center text-sm text-glass-text-muted">{t.home.errorWorkOrder}</div>

  // "Every service is done" and "the job is still ours to hand back" are two
  // different facts. Services stay done after the hand-back, so gating on
  // allDone alone kept the CTA on screen for a FINISHED job and pressing
  // it returned "cannot transition from FINISHED to FINISHED".
  const stillOurs = wo.status === 'IN_PROGRESS'
  const allDone = isAllDone(wo) && stillOurs
  const handedBack = wo.status === 'FINISHED'
  const numbered = wo.services.length > 1
  // Reaching this page at all already means `stake` is true — the backend
  // gates `/work-orders/{id}/detail` on the same predicate. What it decides
  // here is whether a STILL-loose service on the job gets its own "Take
  // service" row, via `WoServiceCard` — never a whole-job claim, which
  // lives on the board card instead.
  const stake = hasStake(wo, meId)
  const expandedId = expandChoice === undefined ? defaultExpandedId(wo) : expandChoice
  const toggle = (serviceId: string) =>
    setExpandChoice((cur) => {
      const base = cur === undefined ? defaultExpandedId(wo) : cur
      return base === serviceId ? null : serviceId
    })

  // Taking ONE loose service, once the tech already holds a stake elsewhere
  // on the job — the per-service counterpart to the board's takeJob
  // (TodayRoute.tsx), which is the only place the whole-job claim lives.
  const takeService = (serviceId: string) => {
    toast.clear()
    claimService.mutate(
      { workOrderId: wo.id, serviceId },
      { onError: (error) => toast.reportError(error, () => takeService(serviceId)) },
    )
  }
  // Optimistic, so a failure is a card that already moved and moved back —
  // the toast is what keeps that from reading as a glitch.
  const startTask = (serviceId: string) => {
    toast.clear()
    start.mutate(serviceId, { onError: (error) => toast.reportError(error, () => startTask(serviceId)) })
  }
  const pauseTask = (serviceId: string) => {
    toast.clear()
    pause.mutate(serviceId, { onError: (error) => toast.reportError(error, () => pauseTask(serviceId)) })
  }
  // One task-scoped flag — see TodayRoute: the optimistic patch swaps which
  // button the card renders, so a direction-specific flag loses its spinner.
  const timerServiceId = start.isPending
    ? start.variables ?? null
    : pause.isPending
      ? pause.variables ?? null
      : null

  return (
    <section aria-label={t.workOrder.regionLabel}>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-transparent px-2 py-1.5 text-13 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
      >
        <ArrowLeft size={16} weight="bold" aria-hidden="true" />
        {t.common.today}
      </button>

      {/*
        Named grid areas so the notes panel can move from "between the
        header and the services" (stacked, below `lg`) to "beside both,
        sticky" (`lg` and up) without duplicating markup for the two
        layouts. Same DOM order either way — the area names just get
        reassigned to cells.
      */}
      <div
        className={cn(
          // One explicit minmax(0,1fr) column below `lg`: an implicit `auto`
          // column sizes to its widest content, and at phone width the
          // header card and the notes pushed the whole page past the
          // viewport (seen live 2026-09-22, 375px).
          'mt-4 grid grid-cols-[minmax(0,1fr)] gap-3',
          "[grid-template-areas:'header'_'notes'_'services']",
          'lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-4',
          "lg:[grid-template-areas:'header_notes'_'services_notes']",
        )}
      >
        <div className="flex flex-col gap-3 [grid-area:header]">
          <WoHeaderCard wo={wo} now={now} />

          {handedBack && (
            <p
              role="status"
              className="rounded-md border-2 border-green bg-green-bg px-4 py-3 text-14 font-bold text-glass-text"
            >
              {t.workOrder.handedBack}
            </p>
          )}
        </div>

        {/* Pinned at `lg`; the thread caps its own height and scrolls its
            list, so the input stays at the bottom of the panel however
            long the conversation gets. */}
        <div className="[grid-area:notes] lg:sticky lg:top-4">
          <WoJobNotes workOrderId={wo.id} notes={wo.notes} readOnly={wo.status === 'CANCELLED'} className="lg:max-h-[calc(100vh-6rem)]" />
        </div>

        <div className="flex flex-col gap-3 [grid-area:services]">
          {allDone ? (
            <WoCompletion
              wo={wo}
              now={now}
              onComplete={() => completeWo.mutate({ workOrderId: wo.id })}
              completing={completeWo.isPending}
              error={completeWo.error}
            />
          ) : (
            <>
              {/* Dispatched with no services yet (2026-09-22) — the office
                  adds them; no completion CTA (isAllDone is false with none
                  countable) and no per-service cards to render. */}
              {wo.services.length === 0 ? (
                <p className="rounded-md border border-glass-border bg-glass-fill p-px18 text-13 text-glass-text-muted">
                  {t.tasks.noServiceYet}
                </p>
              ) : (
                wo.services.map((svc) => (
                  <WoServiceCard
                    key={svc.id}
                    workOrderId={wo.id}
                    service={svc}
                    numbered={numbered}
                    now={now}
                    expanded={expandedId === svc.id}
                    onToggle={() => toggle(svc.id)}
                    onStart={startTask}
                    onPause={pauseTask}
                    timerPending={svc.id === timerServiceId}
                    stake={stake}
                    onTakeService={takeService}
                    takingService={claimService.isPending && claimService.variables?.serviceId === svc.id}
                  />
                ))
              )}

              {/* Withheld this release (featureFlags.ts) — a technician who
                  finds more work tells the manager, who adds the line. */}
              {FEATURE_TECH_ADD_SERVICE && (
              <button
                type="button"
                onClick={() => setProposeOpen(true)}
                className="inline-flex items-center gap-1.5 self-start rounded-full border border-dashed border-glass-border bg-transparent px-4 py-2 text-12-5 font-semibold text-glass-text-muted transition hover:bg-glass-fill-strong hover:text-glass-text"
              >
                <Plus size={13} weight="bold" aria-hidden="true" /> {t.workOrder.additionalService}
              </button>
              )}
            </>
          )}
        </div>
      </div>

      <ProposeServiceModal open={proposeOpen} workOrderId={wo.id} onClose={() => setProposeOpen(false)} />
    </section>
  )
}
